import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, BedDouble, Car, Music, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { BESPOKE_COPY, GOOGLE_FORM_URL, IMAGES, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { BespokeCta } from "./ExperiencesPage";

const DettyGuides = () => {
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setGuides(r.data.filter((p) => p.category === "guide" && p.title.startsWith("Detty December")))).catch(() => {});
  }, []);
  if (!guides.length) return null;
  return (
    <FadeUp className="mt-14" >
      <section className="border border-jawa-gold/40 bg-white p-8 lg:p-10" data-testid="detty-guides-section">
        <p className="text-xs uppercase tracking-[0.3em] text-jawa-coral">New — season guides</p>
        <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-jawa-espresso">The Detty December A–Z.</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-jawa-espresso/65">
          City-specific and genuinely different: Lagos loud, Abuja polished. Restaurants, nightlife, the events
          landscape, beaches and lake days, family options, transport, stays and safety — established venues
          distinguished from annually changing events. Instant PDF download.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {guides.map((p) => (
            <Link
              key={p._id}
              to={`/shop/p/${p._id}`}
              data-testid={`detty-guide-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex overflow-hidden border border-jawa-espresso/10 transition-colors hover:border-jawa-coral/60"
            >
              <div className="relative w-2/5 overflow-hidden">
                <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="flex flex-1 flex-col justify-between p-5">
                <h3 className="font-serif text-lg font-medium leading-snug text-jawa-espresso">{p.title}</h3>
                <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-3">
                  <span className="font-serif text-xl font-medium text-jawa-espresso"><Price amount={p.price} from={p.currency || "USD"} /></span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral">Buy <ArrowUpRight className="h-3.5 w-3.5" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </FadeUp>
  );
};

const SECTIONS = [
  {
    icon: BedDouble,
    title: "Stay",
    body: "Where you sleep sets the tone. From mid-December to the first week of January the best hotels, shortlets and serviced apartments sell out — book by autumn, and book verified. We point you to stays in the right neighbourhoods for your plans, and only to places we would put our own family in.",
  },
  {
    icon: Music,
    title: "Play",
    body: "Lagos becomes the loudest, best-dressed party on earth — concerts, beach days, owambẹ weddings and reunions. Season anchors include Flytime Fest's concert series, The Experience Lagos (one of the world's largest free concerts) and, a road trip away, the Calabar Carnival. Buy event tickets only from official channels.",
    note: "Listings last checked: October 2026. This year's line-ups are added as organisers announce them — we never list unconfirmed events.",
  },
  {
    icon: Car,
    title: "Move",
    body: "The season starts at the airport: the free Nigerian Airport Guide covers immigration, SIM cards and pickups. After that — vetted drivers at night, prices agreed before boats or rides, valuables kept light at crowded events, and a naira budget with a buffer. December pricing is real.",
  },
  {
    icon: ShieldCheck,
    title: "Make it easy",
    body: "JAWA TOURIST bundles your SIM, arrival toolkit and a vetted driver so your first hour is already handled. The Tourist Membership unlocks the members-only A–Z — events etiquette, glam teams and transport intel that take years to learn — and JAWA CONNECT gatherings run all season. Confident beats fearful: Lagos rewards the prepared.",
  },
];

export default function DettyDecemberPage() {
  return (
    <div className="bg-jawa-ivory" data-testid="detty-december-page">
      <Nav />
      <Seo
        title="Detty December Guide — Lagos at Its Loudest | JÀWÀ Africa"
        description="Your JÀWÀ guide to Detty December in Lagos: the events, beach clubs, concerts and logistics that matter — plan the season properly and arrive prepared."
      />
      <header className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={IMAGES.lagosSocial} alt="Detty December in Lagos" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/40 to-jawa-nearblack/25" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-jawa-yellow">The JÀWÀ Seasonal Guide</p>
            <h1 className="mt-3 font-serif text-5xl font-medium tracking-tight text-jawa-ivory sm:text-6xl">
              Detty December
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-ivory/85 md:text-lg">
              The season when Lagos throws open its doors. Stay well, play hard, move smart —
              and let JÀWÀ make it easy.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detty-bespoke-cta"
                className="inline-flex items-center gap-2 bg-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-turquoise hover:text-jawa-nearblack"
              >
                Plan My December <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="/api/guides/airport/file"
                data-testid="detty-airport-guide"
                className="inline-flex items-center gap-2 border border-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise/10"
              >
                Free Airport Guide
              </a>
            </div>
          </FadeUp>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-20 lg:px-10">
        <div className="grid gap-6 md:grid-cols-2">
          {SECTIONS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.05}>
              <section className={`h-full border bg-white p-8 ${s.title === "Make it easy" ? "border-jawa-coral/50" : "border-jawa-stone"}`} data-testid={`detty-section-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <s.icon className="h-6 w-6 text-jawa-coral" />
                <h2 className="mt-4 font-serif text-2xl font-medium tracking-tight text-jawa-espresso">{s.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/70">{s.body}</p>
                {s.note && <p className="mt-3 text-xs italic text-jawa-espresso/45">{s.note}</p>}
              </section>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-14">
          <section className="border border-jawa-gold/40 bg-white p-8 lg:p-10" data-testid="detty-itinerary">
            <SectionHeading overline="Steal this plan" title="A sample December week" />
            <ol className="mt-8 space-y-4">
              {[
                "Day 1 — Land. SIM registered, bags at your verified stay, quiet rooftop dinner.",
                "Day 2 — Island day: Nike Art Gallery, Lekki Arts & Crafts Market, Elegushi at dusk.",
                "Day 3 — Boat day: Tarkwa Bay or Ilashe with a cooler and good company.",
                "Day 4 — Culture: John Randle Centre, Kalakuta Museum, live music at Bogobiri.",
                "Day 5 — Glam day: fittings, gele artist, then the night's concert or owambẹ.",
                "Day 6 — Recovery: spa morning, brunch at The Wheatbaker, beach-club sunset.",
                "Day 7 — Sunday slow: church or brunch, last suya, pack with zero panic.",
              ].map((d, i) => (
                <li key={d} className="flex gap-4 text-sm leading-relaxed text-jawa-espresso/75">
                  <span className="font-serif text-xl font-medium text-jawa-gold">{i + 1}</span>
                  {d}
                </li>
              ))}
            </ol>
            <p className="mt-6 text-xs text-jawa-espresso/50">
              Every itinerary is adapted to your dates, budget and group — arrangements are confirmed
              before anything is booked.
            </p>
          </section>
        </FadeUp>

        <DettyGuides />

        <FadeUp className="mt-14">
          <BespokeCta testid="detty-bespoke-block" />
        </FadeUp>

        <FadeUp className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/membership/tourist"
            data-testid="detty-membership-link"
            className="bg-jawa-espresso px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta"
          >
            Unlock the Tourist A–Z
          </Link>
          <a
            href={waLink("Hello JÀWÀ — I'm planning Detty December!")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detty-whatsapp"
            className="border border-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise/10"
          >
            WhatsApp the Concierge
          </a>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
